
import {
  computed,
  ComputedRef,
  defineComponent,
  onBeforeMount,
  Ref,
  ref,
} from "vue";
import { useStore } from "vuex";
import router from "../../../../src/routes";
import ReceiptModal from "../../components/application/shop/Receipt.vue";
import { useRoute } from "vue-router";
import ModalAlert from "../../components/application/shop/ModalAlert.vue";
import { Product } from "../../../../domain/entities/shop/product/product";
import { Receipt } from "../../../../domain/entities/shop/order/receipt";
import { Order } from "../../../../domain/entities/shop/order/order";
import { User } from "../../../../domain/entities/entities";
import { dispatchErrorBox } from "../../components/mixins";
import { RepositoryException } from "../../../../domain/exceptions/exceptions";
import { wrapLoading } from "../../utils/wrap_loading";
import TUIViewer from "../../components/application/TUIViewer.vue";
import HoverGallery from "../../components/application/shop/HoverGallery.vue";

export default defineComponent({
  components: { HoverGallery, TUIViewer, ModalAlert, ReceiptModal },
  setup: function() {
    const store = useStore();
    const route = useRoute();

    /** Variable used to open or close the checkout modal. */
    const isModalOpened: Ref<boolean> = ref(false);

    /** Variable used to open or close the modal when there is no receipt. */
    const isModalAlertOpened: Ref<boolean> = ref(false);

    /** Variable used to receive products from the updated bag. */
    const bagProductsUpdated: Ref<Array<Product>> = ref([]);

    /** Tells to the bag has unavailable product */
    const hasUnavailableProduct: Ref<boolean> = ref(false);

    /** Checks if the user has enough decoins to buy the product in the bag. */
    const hasUserDecoins: Ref<boolean> = ref(false);

    /** Variable used to check the amount of product stock.*/
    const quantityOfStock: Ref<number> = ref(1);

    /** Number of products added to the bag. */
    const bagProductQuantity: Ref<number> = ref(1);

    /** Checks if the product is in stock. */
    const hasProductStock: Ref<boolean> = ref(false);

    /** Computed from the products in the bag.
     * @see Product */
    const bagProducts: ComputedRef<Array<Product>> = computed(() => {
      return store.getters["shop_module/productsInBag"];
    });

    /** Computed of the modal of actions that were performed successfully.*/
    const success = computed(() => {
      return store.getters["miscellany_module/success"];
    });

    /** Computed from receipt returned at checkout.
     * @see Receipt */
    const receipt: ComputedRef<Receipt> = computed(() => {
      return store.getters["shop_module/receiptOrder"];
    });

    /** Computed that sums the values of the products added to the bag. */
    const amountTotal: ComputedRef<number> = computed(() => {
      const bagProducts = store.getters["shop_module/productsInBag"];

      let total = 0;

      bagProducts.forEach((item) => {
        total += item.decoins * item.quantity;
      });

      return total;
    });

    /** Variable that receives the total sum of products in the bag. */
    const totalQuantityOfProducts: ComputedRef<number> = computed(() => {
      let totalOfProducts = 0;

      bagProducts.value.forEach((item) => {
        totalOfProducts += item.quantity;
      });

      return totalOfProducts;
    });

    /** List of registered products coming from the request. */
    const products: ComputedRef<Array<Product>> = computed(() => {
      return store.getters["shop_module/products"];
    });

    /** Getter of the decoins with the subtraction of the values of the products added in the bag. */
    const balance: ComputedRef<number> = computed(() => {
      return store.getters["shop_module/balance"];
    });

    /** User information getter. */
    const user: ComputedRef<User> = computed(() => {
      return store.getters["userProfile_module/user"];
    });

    onBeforeMount(async () => {
      try {
        await store.dispatch("shop_module/products");
        await store.dispatch("userProfile_module/loadUser");
        await store.dispatch("shop_module/loadBagProducts");
        await store.dispatch(
          "shop_module/setDecoinsInWallet",
          user.value.decoins
        );
        await updateBagProducts();
        if (route.params.orderID) {
          isModalOpened.value = !isModalOpened.value;
        }
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }

      showSeeMoreButton();
    });

    /** Function that returns the product value according to the amount of products added.
     * @param decoins product value.
     * @param quantity quantity of products added. */
    function amount(decoins: number, quantity: number): string {
      const amount = decoins * quantity;
      return formatDecoins(amount);
    }

    /** Function responsible for disabling the button to decrease the amount of product, when the amount is less than or equal to 1.
     * @param quantity quantity of products added. */
    const disabled = (quantity: number): boolean => {
      return quantity <= 1;
    };

    /** Function responsible for disabling the add more products button when the quantity added is equal to the current stock or
     * when the quantity is greater than the stock.
     * @param product parameter that takes an object of type Product. */
    const isProductOutOfStock = (product: Product): boolean => {
      return (
        product.quantity === product.stock || product.quantity > product.stock
      );
    };

    /** Function responsible for checking if the product is still in stock.
     * @param product parameter that takes an object of type Product.*/
    const notStock = (product: Product): boolean => {
      return product.stock === 0;
    };

    /** Function responsible for validating if the amount of products added exceeds the current stock value.
     * @param product parameter that takes an object of type Product.*/
    const exceededStock = (product: Product): boolean => {
      return product.quantity > product.stock && product.stock !== 0;
    };

    /** Function responsible for removing the product from the bag.
     * @param product product to be removed.
     * @see Product */
    async function removeFromBag(product: Product): Promise<void> {
      try {
        await store.dispatch("shop_module/removeFromBag", product);
        await updateBagProducts();
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    /** Function responsible for finalizing the user's purchase.
     * At the end of the purchase, the modal with the order details is opened. */
    async function finishOrder(): Promise<void> {
      const order: Order = new Order(
        totalQuantityOfProducts.value,
        amountTotal.value,
        bagProducts.value
      );

      try {
        if (userDoNotHaveDecoinsEnough()) {
          return;
        } else {
          await wrapLoading(async () => {
            await store.dispatch("shop_module/order", order);
            await store.dispatch("userProfile_module/loadUser");
          });

          await router.push({
            name: "bag",
            params: { orderID: receipt.value.id },
          });

          await store.dispatch("shop_module/removeAllProductsFromBag");
          isModalOpened.value = !isModalOpened.value;
        }
      } catch (e) {
        if (
          e instanceof RepositoryException &&
          e.code === "ERROR_ON_CREATE_RECEIPT"
        ) {
          console.error(e.message);
          isModalAlertOpened.value = !isModalAlertOpened.value;
        } else if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    /** Function responsible for increasing the amount of products in the bag. */
    async function increaseProduct(product: Product): Promise<void> {
      try {
        await store.dispatch("shop_module/increaseProduct", product);
        product.quantity++;
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    /** Function responsible for decreasing the amount of products in the bag. */
    async function decreaseProduct(product: Product): Promise<void> {
      try {
        await store.dispatch("shop_module/decreaseProduct", product);
        hasProductStock.value = false;
        product.quantity--;
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    /** Function responsible for updating the products that were added to the bag. */
    async function updateBagProducts() {
      for (let bagProduct of bagProducts.value) {
        let product = new Product();

        try {
          product = await store.dispatch(
            "shop_module/getProduct",
            bagProduct.id
          );
        } catch (e) {
          if (e instanceof RepositoryException) {
            await dispatchErrorBox(e.message);
          } else {
            await dispatchErrorBox("Ocorreu um erro inesperado");
          }
        }

        try {
          await store.dispatch("shop_module/removeFromBag", bagProduct);
        } catch (e) {
          if (e instanceof RepositoryException) {
            await dispatchErrorBox(e.message);
          } else {
            await dispatchErrorBox("Ocorreu um erro inesperado");
          }
        }

        if (!product || product.isActive === false) {
          bagProduct.isActive = false;
          hasUnavailableProduct.value = true;

          try {
            await store.dispatch("shop_module/addToBag", bagProduct);
          } catch (e) {
            if (e instanceof RepositoryException) {
              await dispatchErrorBox(e.message);
            } else {
              await dispatchErrorBox("Ocorreu um erro inesperado");
            }
          }

          return;
        } else {
          hasUnavailableProduct.value = false;
        }

        const quantityProduct: boolean = checkQuantity(product);
        const decoinsProduct: boolean = checkProductDecoins(product);

        hasUserDecoins.value = quantityProduct || decoinsProduct;

        product.quantity = bagProduct.quantity;
        if (
          product.stock === 0 ||
          (product.quantity > product.stock && product.stock !== 0)
        ) {
          hasProductStock.value = true;
        } else {
          hasProductStock.value = false;
        }

        try {
          await store.dispatch("shop_module/addToBag", product);
        } catch (e) {
          if (e instanceof RepositoryException) {
            await dispatchErrorBox(e.message);
          } else {
            console.error(e);
            await dispatchErrorBox("Ocorreu um erro inesperado.");
          }
        }
      }
    }

    /** Function used to control whether for each product added to the bag it is still possible for the user to add
     * the other products that are available
     * @param product product to do validation. */
    function checkEnough(product: Product) {
      if (product.quantity >= product.stock) {
        return false;
      }
      if (product.decoins > user.value.decoins && product.quantity === 1) {
        return true;
      }
      return balance.value < product.decoins;
    }

    /** Function responsible for checking if the value of the product is greater than the amount of user decoins.
     * @param product product to do validation. */
    function checkProductDecoins(product: Product): boolean {
      return product.decoins > user.value.decoins;
    }

    /** Function used to validate if the amount of added products exceeds the user's decoins.
     * @param product product to do validation. */
    function checkQuantity(product: Product): boolean {
      if (product.quantity > 1 && product.decoins <= user.value.decoins) {
        if (product.quantity * product.decoins > user.value.decoins) {
          return true;
        }
      }
      return false;
    }

    /** Function responsible for checking if the checkout button should be disabled. */
    const shouldButtonDisabled = computed(() => {
      const userDecoins = userDoNotHaveDecoinsEnough();

      if (hasUnavailableProduct.value) {
        return true;
      }

      if (hasProductStock.value) {
        return true;
      }

      if (hasUserDecoins.value) {
        return true;
      }

      if (userDecoins) {
        return true;
      }

      return false;
    });

    /** Function responsible for checking if the purchase total is greater than the user's decoins. */
    function userDoNotHaveDecoinsEnough(): boolean {
      return amountTotal.value > user.value.decoins;
    }

    /** Function responsible for formatting the decoins to Brazilian currency. */
    function formatDecoins(decoins: number) {
      return Intl.NumberFormat("pt-br").format(decoins);
    }

    /** Function responsible for checking if the product was removed from the shop. */
    function checkProductIsActive(product: Product): boolean {
      return product.isActive === false;
    }

    /** Function to toggle the description visibility between "see more" and "see less" */
    function seeMoreDescription(index: number): void {
      const buttonReadMore = document.querySelector(
        `[data-see-more="${index}"]`
      ) as HTMLButtonElement;
      const description = document.querySelector(
        `[data-description="${index}"]`
      ) as HTMLParagraphElement;

      if (buttonReadMore && description) {
        if (buttonReadMore.innerText === "ver mais") {
          buttonReadMore.innerText = "ver menos";
          description.classList.remove("dots");
        } else {
          buttonReadMore.innerText = "ver mais";
          description.classList.add("dots");
        }
      }
    }

    /** Function to display the "see more" button if the description is too long */
    function showSeeMoreButton(): void {
      if (!products.value) return;
      products.value.forEach((product: Product, index: number) => {
        const buttonReadMore = document.querySelector(
          `[data-see-more="${index}"]`
        ) as HTMLButtonElement;
        const description = document.querySelector(
          `[data-description="${index}"]`
        ) as HTMLParagraphElement;

        if (description.offsetHeight > 100) {
          buttonReadMore.style.display = "flex";
          description.classList.add("dots");
        } else buttonReadMore.style.display = "none";
      });
    }

    return {
      bagProducts,
      success,
      quantityOfStock,
      isModalOpened,
      isModalAlertOpened,
      amountTotal,
      products,
      bagProductsUpdated,
      bagProductQuantity,
      balance,
      shouldButtonDisabled,
      amount,
      disabled,
      removeFromBag,
      finishOrder,
      isProductOutOfStock,
      increaseProduct,
      decreaseProduct,
      exceededStock,
      notStock,
      checkEnough,
      checkProductDecoins,
      checkQuantity,
      userDoNotHaveDecoinsEnough,
      formatDecoins,
      checkProductIsActive,
      seeMoreDescription,
      showSeeMoreButton,
    };
  },
});
